import { Component, computed, inject, OnInit, signal, Signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { CracMapState, PrepareData } from './states/cracmap.state';
import { CRACMAP_CONSTANTS } from './assets/constants';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Geometry, LineString } from 'geojson';
import { SidenavService } from '../../../shared/services/sidenav.service';
import { OrganizationState } from '../../admin/organization/organization.state';
import { AuthState } from '../../../core/states/auth.state';
import { GeoMapCardGeometry } from 'src/app/shared/components/geo-map-card/geo-map-card.component';
import { CircleMarkerOptions, PathOptions } from 'leaflet';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
} from 'src/app/shared/components/map/map.constants';

export interface CracMapData {
    //Minimum of a line
    id?: string;
    year: number;
    error?: boolean;
    territoryId?: number;
    territoryLabel?: string;
    isCircle?: boolean;
    geom?: Geometry;
    critbTcc?: number;
    critbHix?: number;
    critbHixMaNN1?: number;
    critbHixMaNN1N2?: number;
    txCma?: number;
    tauxReclaEnedis?: number;
    tauxReclaEdf?: number;
    densiteUsagerAuKm?: number;
}

const CORSE = [20, '2A', '2B'];
const MARTINIQUE = 972;
const GUADELOUPE = 971;
const REUNION = 974;
const MAYOTTE = 976;
const GUYANE = 973;
const DROM = [...CORSE, MARTINIQUE, GUADELOUPE, REUNION, MAYOTTE, GUYANE];

@UntilDestroy()
@Component({
    selector: 'app-cracmap',
    templateUrl: './cracmap.component.html',
    styleUrls: ['./cracmap.component.scss'],
})
export class CracmapComponent implements OnInit {
    public _currentOrganization$ = inject(Store).select(OrganizationState.currentOrganization);
    public _currentYear$ = inject(Store).select(OrganizationState.currentYear);
    public _loggedUser$ = inject(Store).select(AuthState.loggedUser);

    public size1 = 'calc(82vh)';
    public size2 = 'calc(( 82vh - 156px) /2)';
    public size3 = 'calc(( 82vh - 387px) /2)';
    public size4 = 'calc(( 82vh - 285px) /4)';
    public texts = CRACMAP_CONSTANTS;
    public centerLyon = this.texts.centerLyon;
    public centerParis = this.texts.centerParis;
    public title = this.texts.title;
    public subTitle = this.texts.subTitle;
    public indicators = this.texts.indicators;
    public explanationText = this.texts.explanationText;
    public callToActionText = this.texts.callToActionText;
    public baseMapUrl =
        'https://data.geopf.fr/wmts?layer=ORTHOIMAGERY.ORTHOPHOTOS&style=normal&tilematrixset=PM&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fjpeg&TileMatrix={z}&TileCol={x}&TileRow={y}';

    public cracMapData: Signal<CracMapData[]> = this.store.selectSignal(
        CracMapState.getDatas(),
    ) as Signal<CracMapData[]>;

    public metropolitanBorder$ = inject(Store).select(CracMapState.metropolitanBorder);
    public metropolitanBorder = signal({
        type: 'LineString',
        coordinates: [],
    } as LineString);

    public currentIndicator = signal(this.indicators[0]);

    public geometries = computed(() =>
        this.cracMapData()
            .filter((t) => t.geom)
            .map((t: CracMapData) => {
                const { color } = this.getLegend(
                    t[this.currentIndicator().value as keyof CracMapData],
                );
                return {
                    ...t,
                    organizationId: '1',
                    value: t[this.currentIndicator().value as keyof CracMapData],
                    color,
                    layerOrder: t.isCircle ? 2 : 1,
                    geom: {
                        type: t.isCircle ? 'Point' : t.geom?.type,
                        ...t.geom,
                    },
                } as GeoMapCardGeometry;
            }),
    );
    public metropolitanGeometries = computed(() =>
        this.geometries()
            .filter((g) => !DROM.includes(g['territoryId']))
            .concat([
                {
                    organizationId: '1',
                    isBorder: true,
                    value: null,
                    year: 1,
                    color: '',
                    layerOrder: 0,
                    geom: this.metropolitanBorder(),
                },
            ]),
    );

    public corseGeometries = computed(() =>
        this.geometries().filter((g) => CORSE.includes(g['territoryId'])),
    );
    public martiniqueGeometries = computed(() =>
        this.geometries().filter((g) => g['territoryId'] === MARTINIQUE),
    );
    public guadeloupeGeometries = computed(() =>
        this.geometries().filter((g) => g['territoryId'] === GUADELOUPE),
    );
    public reunionGeometries = computed(() =>
        this.geometries().filter((g) => g['territoryId'] === REUNION),
    );
    public mayotteGeometries = computed(() =>
        this.geometries().filter((g) => g['territoryId'] === MAYOTTE),
    );
    public guyaneGeometries = computed(() =>
        this.geometries().filter((g) => g['territoryId'] === GUYANE),
    );

    constructor(
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        this.sidenavService.setTopMenuItems([]);
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareData());
            });

        this.metropolitanBorder$
            .pipe(untilDestroyed(this))
            .subscribe((d: LineString) => this.metropolitanBorder.set(d));
    }

    public updateCurrentIndicator(key: any) {
        const indicator = this.indicators.find((i) => i.value === key);
        if (indicator && key != this.currentIndicator().value) {
            this.currentIndicator.set(indicator);
        }
    }

    public getLegend(value: any) {
        const legendConfig = this.currentIndicator().legendConfig;

        //Default last Value
        let color = legendConfig[legendConfig.length - 1].color;

        let legend = legendConfig.find((l) => l.min <= value && value < l.max);
        if (legend && value) color = legend.color;

        return { color };
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions | CircleMarkerOptions {
        if (!feature?.properties) {
            return EmptyPolygonStyle;
        }

        if (feature.properties['isBorder']) {
            return {
                ...DefaultPolygonStyle,
                fillOpacity: 0,
                weight: 2,
                opacity: 0.9,
            };
        }

        const color = feature.properties['color'];
        if (!feature.properties['isCircle']) {
            return {
                ...DefaultPolygonStyle,
                fillColor: color,
                fillOpacity: 1,
                opacity: 1,
            };
        } else {
            return {
                radius: 8,
                weight: 1,
                fillColor: color,
                opacity: 1,
                fillOpacity: 1,
                color: 'black',
            };
        }
    }

    public getTooltip(feature: GeoJSON.Feature): string | null {
        if (!feature.properties) {
            return 'Donnée non disponible';
        }
        if (feature.properties['isBorder']) {
            return null;
        }
        let { territoryId, territoryLabel } = feature.properties;
        return `${territoryLabel} (${territoryId.toString().padStart(2, '0')})`;
    }
}
